"use client";

import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { useRouter, usePathname } from "next/navigation";
import { sendEvent, EventNames } from "@/lib/utils/analytics";
import { type TouchpointType, getExperimentVariant } from "@/lib/quiz-attribution";
import { QUIZ_VARIANT_TEST_A, QUIZ_VARIANT_TEST_B } from "@/lib/utils/common/constants";
import { QuizExplainer } from "./_components/explainer";
import { QuizFetchingResult } from "./_components/fetching-result";
import { QuizHeader } from "./_components/header";
import { QuizProgressBar } from "./_components/progress-bar";
import { QuizQuestion } from "./_components/question";
import { QuizUserDetails } from "./_components/user-details";
import { CrossIcon } from "../ui/icons/cross-icon";
import {
  getMatchingExplainers,
  getNextQuestion,
  getProgress,
  getQuizRestoredState,
  resetQuizState,
  setQuizState,
  FIRST_QUESTION_ID,
  questions,
  getResultPageType,
} from "./utils/utils";
import { sendQuizStepEvent } from "@/lib/api/quiz";
import { QUIZ_SOURCE, QUIZ_DISC_CODE } from "./constants";
import type { QuizState, QuestionHistoryEntry, ExplainerEntry } from "./types";
import { setCookie } from "@/lib/utils/common/common";
import { Button } from "@workspace/ui/components/button";
import { useModal } from "../providers/modal-provider";
import { modalIds } from "../modals/constants";
import HormoneChartHero from "./results/HormoneChartHero";
import WhyTrackHormones from "./results/WhyTrackHormones";
import ProductHighlight from "./results/ProductHighlight";
import WhatMakesInitoDifferent from "./results/WhatMakesInitoDifferent";
import TrackHormoneValues from "./results/TrackHormoneValues";
import WhyMeasureProgesterone from "./results/WhyMeasureProgesterone";
import SpectralPrecision from "./results/SpectralPrecision";
import InitoTracksEverything from "./results/InitoTracksEverything";
import FertilityLab from "./results/FertilityLab";
import FansSection from "./results/FansSection";
import FAQ from "./results/FAQ";
import Footer from "./results/Footer";
import StickyFooterCta from "./results/StickyFooterCta";
import { SCROLL_MILESTONES } from "./constants";

export function QuizRenderer({ touchpointType, onQuizCompleted }: { touchpointType?: TouchpointType; onQuizCompleted?: (completed: boolean) => void }) {
  const [state, setState] = useState<QuizState>(getQuizRestoredState() as QuizState);
  const router = useRouter();
  const pathname = usePathname();
  const { handleOpenModal, handleCloseModal } = useModal();

  const resultScrollRef = useRef<HTMLDivElement>(null);
  const scrollMilestonesRef = useRef(new Set<number>());
  const quizVariant = getExperimentVariant();
  const defaultResultPageType = quizVariant === QUIZ_VARIANT_TEST_B
    ? "website_quiz_integration_v2"
    : "website_quiz_integration_v1";
  const resultPageTypeRef = useRef<string>(defaultResultPageType);

  const isQuestion = useMemo(() => state.view === "question", [state.view]);
  const isExplainer = useMemo(() => state.view === "explainer", [state.view]);
  const isUserDetails = useMemo(() => state.view === "userDetails", [state.view]);
  const isFetchingResult = useMemo(() => state.view === "fetchingResult", [state.view]);
  const isResult = useMemo(() => state.view === "result", [state.view]);

  const isQuizCompleted = isResult || isFetchingResult;

  useEffect(() => {
    onQuizCompleted?.(isQuizCompleted);
  }, [isQuizCompleted, onQuizCompleted]);
  
  const showHeader = useMemo(() => isQuestion || isExplainer || isUserDetails, [isQuestion, isExplainer, isUserDetails]);
  const showProgress = useMemo(() => isQuestion, [isQuestion]);
  const progress = useMemo(() => getProgress(state), [state]);

  const isFirstQuestion = useMemo(() => {
    return state.currentQuestionId === FIRST_QUESTION_ID;
  }, [state.currentQuestionId]);

  const canGoBack = useMemo(() => {
    return !isFirstQuestion || isExplainer;
  }, [isFirstQuestion, isExplainer]);

  const currentQuestion = useMemo(() => questions.find(
    (q) => q.id === state.currentQuestionId
  ), [state.currentQuestionId]);

  const hasVisitedBefore = useMemo(() => state.questionHistory.some(
    (h) => h.questionId === state.currentQuestionId
  ), [state.questionHistory, state.currentQuestionId]);


  const fireViewEvent = useCallback((view: QuizState["view"], questionId?: string) => {
    if (view === "question") {
      sendEvent(EventNames.QUESTION_VIEWED, {
        quiz_source: QUIZ_SOURCE,
        touchpoint_type: touchpointType,
        question_id: questionId,
      });
      if (questionId !== FIRST_QUESTION_ID) {
        sendEvent(EventNames.QUIZ_INTERMEDIATE_PAGE_LOAD, {
          quiz_source: QUIZ_SOURCE,
          touchpoint_type: touchpointType,
          url: window.location.href,
        });
      }
    } else if (view === "explainer") {
      sendEvent(EventNames.QUESTION_VIEWED, {
        quiz_source: QUIZ_SOURCE,
        touchpoint_type: touchpointType,
        question_id: questionId,
      });
      sendEvent(EventNames.QUIZ_INTERMEDIATE_PAGE_LOAD, {
        quiz_source: QUIZ_SOURCE,
        touchpoint_type: touchpointType,
        url: window.location.href,
      });
    } else if (view === "fetchingResult") {
      sendEvent(EventNames.QUIZ_INTERMEDIATE_PAGE_LOAD, {
        quiz_source: QUIZ_SOURCE,
        touchpoint_type: touchpointType,
        url: window.location.href,
      });
    } else if (view === "userDetails") {
      sendEvent(EventNames.USER_DETAILS_PAGE_LOADED, {
        quiz_source: QUIZ_SOURCE,
        touchpoint_type: touchpointType,
        url: window.location.href,
      });
    } else if (view === "result") {
      const pageType = getResultPageType(state.results);
      resultPageTypeRef.current = pageType;
      scrollMilestonesRef.current.clear();
      sendEvent(EventNames.QUIZ_RESULT_PAGE_LOADED, {
        quiz_source: QUIZ_SOURCE,
        touchpoint_type: touchpointType,
        page_type: pageType,
      });
    }
  }, [touchpointType, state.results]);

  const handleResultScroll = useCallback(() => {
    const container = resultScrollRef.current;
    if (!container) return;
    const { scrollTop, scrollHeight, clientHeight } = container;
    if (scrollHeight <= clientHeight) return;
    const scrollPercent = Math.round((scrollTop / (scrollHeight - clientHeight)) * 100);
    for (const milestone of SCROLL_MILESTONES) {
      if (scrollPercent >= milestone && !scrollMilestonesRef.current.has(milestone)) {
        scrollMilestonesRef.current.add(milestone);
        sendEvent(EventNames.QUIZ_RESULT_PAGE_SCROLLED, {
          quiz_source: QUIZ_SOURCE,
          touchpoint_type: touchpointType,
          percentage_scrolled: milestone,
          page_type: resultPageTypeRef.current,
        });
      }
    }
  }, []);

  const storeLastQuestionIndex = useCallback((questionId: string) => {
    const questionIndex = questions.findIndex((q) => q.id === questionId);
    setQuizState({
      lastQuestion: questionIndex >= 0 ? questionIndex : 0,
    });
  }, []);

  const handleBack = () => {
    if (isResult) {
      const last = state.questionHistory[state.questionHistory.length - 1];
      if (last) {
        storeLastQuestionIndex(last.questionId);
        setQuizState({ lastSlideType: "question", status: 1 });
        setState((prev) => ({
          ...prev,
          view: "question",
          currentQuestionId: last.questionId,
          selectedOptions: last.selectedOptionIds,
        }));
        fireViewEvent("question", last.questionId);
      }
      return;
    }
    if (isExplainer) {
      if (state.currentExplainerIndex > 0) {
        const prevIndex = state.currentExplainerIndex - 1;
        setQuizState({ lastExplainer: prevIndex });
        setState((prev) => ({
          ...prev,
          currentExplainerIndex: prevIndex,
        }));
      } else {
        const last = state.questionHistory[state.questionHistory.length - 1];
        if (last) {
          storeLastQuestionIndex(last.questionId);
          setQuizState({ lastSlideType: "question", explainersHistory: [], lastExplainer: 0 });
          setState((prev) => ({
            ...prev,
            view: "question",
            currentQuestionId: last.questionId,
            currentExplainerIndex: 0,
            currentExplainers: [],
            selectedOptions: last.selectedOptionIds,
          }));
          fireViewEvent("question", last.questionId);
        }
      }
    } else if (isUserDetails) {
      const last = state.questionHistory[state.questionHistory.length - 1];
      if (last) {
        storeLastQuestionIndex(last.questionId);
        setQuizState({ lastSlideType: "question" });
        setState((prev) => ({
          ...prev,
          view: "question",
          currentQuestionId: last.questionId,
          selectedOptions: last.selectedOptionIds,
        }));
        fireViewEvent("question", last.questionId);
      }
    } else if (isQuestion && state.questionHistory.length > 0) {
      const currentIdx = state.questionHistory.findIndex(
        (h) => h.questionId === state.currentQuestionId
      );
      const target = currentIdx > 0
        ? state.questionHistory[currentIdx - 1]
        : currentIdx === -1
          ? state.questionHistory[state.questionHistory.length - 1]
          : undefined;

      if (target) {
        const targetIdx = state.questionHistory.findIndex(
          (h) => h.questionId === target.questionId
        );
        const truncatedHistory = state.questionHistory.slice(0, targetIdx + 1);

        storeLastQuestionIndex(target.questionId);
        setQuizState({ lastSlideType: "question", questionHistory: truncatedHistory });
        setState((prev) => ({
          ...prev,
          questionHistory: truncatedHistory,
          currentQuestionId: target.questionId,
          selectedOptions: target.selectedOptionIds,
        }));
        fireViewEvent("question", target.questionId);
      }
    }
  };

  const handleOptionSelect = (optionId: number) => {
    const currentQuestion = questions.find(
      (q) => q.id === state.currentQuestionId
    );
    if (!currentQuestion) return;

    if (currentQuestion.multi_select) {
      const isNoneOption = (id: number) =>
        currentQuestion.options.find((o) => o.id === id)?.option.toLowerCase() === "none";

      setState((prev) => {
        if (prev.selectedOptions.includes(optionId)) {
          return { ...prev, selectedOptions: prev.selectedOptions.filter((id) => id !== optionId) };
        }
        if (isNoneOption(optionId)) {
          return { ...prev, selectedOptions: [optionId] };
        }
        return {
          ...prev,
          selectedOptions: [...prev.selectedOptions.filter((id) => !isNoneOption(id)), optionId],
        };
      });
    } else {
      setState((prev) => ({ ...prev, selectedOptions: [optionId] }));
    }
  };

  const handleQuestionNext = () => {
    const { currentQuestion, nextQuestion, nextQuestionId } = getNextQuestion(
      state.currentQuestionId,
      state.selectedOptions
    );
    if (!currentQuestion || state.selectedOptions.length === 0) return;

    const quizData = state.questionHistory.reduce<Record<string, number[]>>(
      (acc, entry) => {
        acc[entry.questionId] = entry.selectedOptionIds;
        return acc;
      },
      { [currentQuestion.id]: state.selectedOptions },
    );
    sendQuizStepEvent(currentQuestion as unknown as Record<string, unknown>, quizData);

    if (state.currentQuestionId === FIRST_QUESTION_ID) {
      sendEvent(EventNames.NEXT_BUTTON_CLICKED, {
        quiz_source: QUIZ_SOURCE,
        touchpoint_type: touchpointType,
      });
    }

    const postExplainerView: "question" | "fetchingResult" = nextQuestion
      ? "question"
      : "fetchingResult";

    const matchingExplainers = getMatchingExplainers(
      currentQuestion.explainers as ExplainerEntry[],
      state.selectedOptions
    );

    const currentEntry: QuestionHistoryEntry = {
      questionId: currentQuestion.id,
      selectedOptionIds: state.selectedOptions,
    };
    const existingIdx = state.questionHistory.findIndex(
      (h) => h.questionId === currentQuestion.id
    );

    let newHistory: QuestionHistoryEntry[];
    if (existingIdx >= 0) {
      const nextInHistory = state.questionHistory[existingIdx + 1];
      const pathChanged = nextInHistory && nextQuestionId && nextInHistory.questionId !== nextQuestionId;
      newHistory = [
        ...state.questionHistory.slice(0, existingIdx),
        currentEntry,
        ...(pathChanged ? [] : state.questionHistory.slice(existingIdx + 1)),
      ];
    } else {
      newHistory = [...state.questionHistory, currentEntry];
    }

    const nextSavedOpts = nextQuestionId
      ? newHistory.find((h) => h.questionId === nextQuestionId)?.selectedOptionIds ?? []
      : [];

    if (nextQuestionId) {
      const nextIndex = questions.findIndex((q) => q.id === nextQuestionId);
      if (nextIndex >= 0)
        setQuizState({ lastQuestion: nextIndex, questionHistory: newHistory });
    } else {
      setQuizState({ questionHistory: newHistory });
    }

    if (matchingExplainers.length > 0) {
      setQuizState({
        lastSlideType: "explainer",
        explainersHistory: matchingExplainers,
        lastExplainer: 0,
        questionHistory: newHistory,
      });
      setState((prev) => ({
        ...prev,
        isFreshStart: false,
        questionHistory: newHistory,
        currentQuestionId: nextQuestionId ?? prev.currentQuestionId,
        view: "explainer",
        currentExplainerIndex: 0,
        currentExplainers: matchingExplainers,
        selectedOptions: nextSavedOpts,
        postExplainerView,
      }));
      fireViewEvent("explainer", matchingExplainers[0]);
    } else if (nextQuestion) {
      setQuizState({
        lastSlideType: "question",
        questionHistory: newHistory,
      });
      setState((prev) => ({
        ...prev,
        isFreshStart: false,
        questionHistory: newHistory,
        currentQuestionId: nextQuestionId!,
        selectedOptions: nextSavedOpts,
      }));
      fireViewEvent("question", nextQuestionId!);
    } else {
      setQuizState({
        status: 2,
        completedAt: Date.now(),
        lastSlideType: "result",
        questionHistory: newHistory,
      });
      setState((prev) => ({
        ...prev,
        isFreshStart: false,
        questionHistory: newHistory,
        view: "fetchingResult",
        selectedOptions: [],
      }));
      fireViewEvent("fetchingResult");
    }
  };

  const handleExplainerNext = () => {
    if (state.currentExplainerIndex < state.currentExplainers.length - 1) {
      const nextIndex = state.currentExplainerIndex + 1;
      setQuizState({ lastExplainer: nextIndex });
      setState((prev) => ({
        ...prev,
        currentExplainerIndex: nextIndex,
      }));
      fireViewEvent("explainer", state.currentExplainers[nextIndex]);
    } else if (state.postExplainerView === "fetchingResult") {
      setQuizState({
        status: 2,
        completedAt: Date.now(),
        lastSlideType: "result",
        lastExplainer: 0,
        explainersHistory: [],
      });
      setState((prev) => ({
        ...prev,
        view: "fetchingResult",
        currentExplainerIndex: 0,
        currentExplainers: [],
      }));
      fireViewEvent("fetchingResult");
    } else {
      const nextView = state.postExplainerView;
      setQuizState({
        lastSlideType: nextView,
        lastExplainer: 0,
        explainersHistory: [],
      });
      setState((prev) => ({
        ...prev,
        view: prev.postExplainerView,
        currentExplainerIndex: 0,
        currentExplainers: [],
      }));
      fireViewEvent(nextView, state.currentQuestionId);
    }
  };

  const handleUserDetailsSubmit = (name: string, email: string) => {
    sendEvent(EventNames.USER_INFO_SUBMITTED, {
      quiz_source: "bottom_sheet",
      touchpoint_type: touchpointType,
      url: window.location.href,
    });
    setQuizState({ status: 2, completedAt: Date.now(), lastSlideType: "result", config: { name, email } });
    setState((prev) => ({
      ...prev,
      view: "fetchingResult",
      config: { name, email },
    }));
    fireViewEvent("fetchingResult");
  };

  const handleFetchingComplete = useCallback(() => {
    setQuizState({ status: 2, completedAt: Date.now() });
    setState((prev) => ({ ...prev, view: "result" }));
    fireViewEvent("result");
  }, [fireViewEvent]);

  const handleRestart = () => {
    sendEvent(EventNames.QUIZ_RESTART_CLICKED, {
      quiz_source: QUIZ_SOURCE,
      touchpoint_type: touchpointType,
    });
    resetQuizState();
    setState({
      view: "question",
      currentQuestionId: FIRST_QUESTION_ID,
      currentExplainerIndex: 0,
      currentExplainers: [],
      questionHistory: [],
      selectedOptions: [],
      postExplainerView: "question",
      isFreshStart: true,
      config: {},
      results: undefined,
    });
    fireViewEvent("question", FIRST_QUESTION_ID);
  };

  const handleBuyNowRedirect = useCallback(() => {
    setCookie("disc_code", QUIZ_DISC_CODE, 7);
    const isOnBuyNow = pathname === "/buy-now";
    if (isOnBuyNow) {
      handleCloseModal();
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      router.push(`/buy-now?disc_code=${QUIZ_DISC_CODE}`);
      setTimeout(() => {
        handleCloseModal();
      }, 300);
    }
  }, [pathname, router, handleCloseModal]);

  useEffect(() => {
    if (state.view === "result") {
      sendEvent(EventNames.QUIZ_RESULTS_REOPENED, {
        quiz_source: QUIZ_SOURCE,
        touchpoint_type: touchpointType,
      });
      return;
    }
    const viewId = state.view === "explainer"
      ? state.currentExplainers[state.currentExplainerIndex]
      : state.currentQuestionId;
    fireViewEvent(state.view, viewId);
  }, []);

  useEffect(() => {
    if (state.isFreshStart) {
      sendEvent(EventNames.QUIZ_PAGE_LOADED, {
        quiz_source: QUIZ_SOURCE,
        touchpoint_type: touchpointType,
        consent_checkbox_visible: true,
      });
    }
  }, []);

  if (isResult) {
    return (
      <div
        ref={resultScrollRef}
        onScroll={handleResultScroll}
        className="flex flex-col overflow-y-auto overflow-x-hidden h-dvh sm:h-full no-scrollbar pb-20"
      >
        <HormoneChartHero onRestart={handleRestart} onBack={handleBack} onClose={handleCloseModal} />
        <WhyTrackHormones />
        <ProductHighlight onCtaClick={handleBuyNowRedirect} touchpointType={touchpointType} />
        <WhatMakesInitoDifferent onCtaClick={handleBuyNowRedirect} touchpointType={touchpointType} />
        <TrackHormoneValues />
        <WhyMeasureProgesterone />
        <SpectralPrecision />
        <InitoTracksEverything />
        <FertilityLab />
        <FansSection />
        <FAQ touchpointType={touchpointType} />
        <Footer />
        <StickyFooterCta onCtaClick={handleBuyNowRedirect} touchpointType={touchpointType} />
      </div>
    );
  }

  if (isFetchingResult) {
    return (
      <div className="flex flex-col h-dvh px-4 sm:min-h-0 sm:h-auto">
        <QuizFetchingResult
          config={state.config}
          questionHistory={state.questionHistory}
          onComplete={handleFetchingComplete}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-dvh max-w-[500px] mx-auto px-6 sm:min-h-0 sm:h-auto">
      {showHeader && (
        <div className="sticky top-0 z-50 bg-white mt-3">
          <div className="flex items-center py-2 gap-10">
            <QuizHeader canGoBack={canGoBack} onBack={handleBack} />
            {showProgress && <QuizProgressBar progress={progress} />}
            <Button variant="ghost" size="icon" onClick={() => handleOpenModal(modalIds.quizExit)} className="shrink-0 ml-auto">
              <CrossIcon className="size-7 aspect-square cursor-pointer" />
            </Button>
          </div>
        </div>
      )}

      {isQuestion && currentQuestion && (
        <QuizQuestion
          isFreshStart={state.isFreshStart}
          currentQuestion={currentQuestion as any}
          selectedOptions={state.selectedOptions}
          hasVisitedBefore={hasVisitedBefore}
          onOptionSelect={handleOptionSelect}
          onNext={handleQuestionNext}
        />
      )}
      {isExplainer && (
        <QuizExplainer
          currentExplainerIndex={state.currentExplainerIndex}
          currentExplainers={state.currentExplainers}
          onNext={handleExplainerNext}
        />
      )}
      {isUserDetails && (
        <QuizUserDetails onSubmit={handleUserDetailsSubmit} />
      )}
    </div>
  );
}
